<template>
  <div class="shop-details">
    <!-- 顶部操作栏 -->
    <div class="action-bar">
      <div class="left-actions">
        <el-button icon="el-icon-back" size="medium" plain @click="goBack">返回列表</el-button>
      </div>
      <div class="date-info" :class="{ 'deleted': shopData.is_deleted === 1 }">
        <div v-if="creatorInfo.username">创建时间：{{ creatorInfo.username }} {{ formatDateTime(shopData.created_at) }}</div>
        <div v-if="updaterInfo.username">{{ shopData.is_deleted === 1 ? '删除时间：' : '上次更新：' }}{{ updaterInfo.username }} {{ formatDateTime(shopData.updated_at) }}</div>
      </div>
      <div class="right-actions" v-if="shopData.is_deleted === 0">
        <el-button type="danger" icon="el-icon-delete" size="medium" plain @click="handleDelete">删除商品</el-button>
      </div>
      <div class="right-actions" v-if="shopData.is_deleted === 1">
        <el-button type="success" icon="el-icon-refresh" size="medium" plain @click="handleRestore">恢复商品</el-button>
      </div>
    </div>

    <!-- 主要内容区 -->
    <div class="main-content">
      <div class="content-grid">
        <!-- 左侧内容列 -->
        <div class="left-column">
          <!-- 标题卡片 -->
          <el-card class="title-card">
            <div class="card-header">
              <span>商品标题</span>
              <div class="header-actions">
                <template v-if="!editingTitle">
                  <el-button type="text" icon="el-icon-edit" @click="startEditTitle">编辑</el-button>
                </template>
                <template v-else>
                  <el-button type="text" icon="el-icon-check" @click="saveTitle">保存</el-button>
                  <el-button type="text" icon="el-icon-close" @click="cancelEditTitle">取消</el-button>
                </template>
              </div>
            </div>
            <div class="title-grid" :class="{ 'is-editing': editingTitle }">
              <div class="title-cn">
                <div class="title-label">中文标题</div>
                <template v-if="!editingTitle">
                  <div class="title-content">{{ shopData.title_cn }}</div>
                </template>
                <template v-else>
                  <el-input v-model="titleEdit.title_cn" placeholder="请输入中文标题"></el-input>
                </template>
              </div>
              <div class="title-ug">
                <div class="title-label">ئۇيغۇرچە تېمىسى</div>
                <template v-if="!editingTitle">
                  <div class="title-content">{{ shopData.title_ug }}</div>
                </template>
                <template v-else>
                  <el-input v-model="titleEdit.title_ug" placeholder="ئۇيغۇرچە تېمىسىنى كىرگۈزۈڭ"></el-input>
                </template>
              </div>
            </div>
          </el-card>

          <!-- 基本信息卡片 -->
          <el-card class="info-card">
            <div class="card-header">
              <span>基本信息</span>
              <div class="header-actions">
                <template v-if="!editingInfo">
                  <el-button type="text" icon="el-icon-edit" @click="startEditInfo">编辑</el-button>
                </template>
                <template v-else>
                  <el-button type="text" icon="el-icon-check" @click="saveInfo">保存</el-button>
                  <el-button type="text" icon="el-icon-close" @click="cancelEditInfo">取消</el-button>
                </template>
              </div>
            </div>
            <div class="info-grid" :class="{ 'is-editing': editingInfo }">
              <div class="info-row">
                <div class="info-item">
                  <div class="info-label">商品状态</div>
                  <template v-if="!editingInfo">
                    <div class="info-content">
                      <el-tag :type="getStatusType(shopData.status)">
                        {{ getStatusText(shopData.status) }}
                      </el-tag>
                    </div>
                  </template>
                  <template v-else>
                    <el-select v-model="infoEdit.status" placeholder="请选择商品状态" style="width: 100%">
                      <el-option
                        v-for="(item, id) in statusMap"
                        :key="id"
                        :label="item.text"
                        :value="Number(id)">
                      </el-option>
                    </el-select>
                  </template>
                </div>

                <div class="info-item">
                  <div class="info-label">是否需要地址</div>
                  <template v-if="!editingInfo">
                    <div class="info-content">
                      <el-tag :type="shopData.need_addr ? 'success' : 'info'">
                        {{ shopData.need_addr ? '需要' : '不需要' }}
                      </el-tag>
                    </div>
                  </template>
                  <template v-else>
                    <el-select v-model="infoEdit.need_addr" placeholder="请选择是否需要地址" style="width: 100%">
                      <el-option :value="true" label="需要"></el-option>
                      <el-option :value="false" label="不需要"></el-option>
                    </el-select>
                  </template>
                </div>
              </div>

              <div class="info-row">
                <div class="info-item">
                  <div class="info-label">商品价格</div>
                  <template v-if="!editingInfo">
                    <div class="info-content price-display">
                      <span class="price-symbol">¥</span>
                      <span class="price-value">{{ shopData.price }}</span>
                    </div>
                  </template>
                  <template v-else>
                    <el-input-number 
                      v-model="infoEdit.price" 
                      :min="0" 
                      :precision="2" 
                      style="width: 100%"
                      :controls-position="'right'"
                      class="price-input">
                    </el-input-number>
                  </template>
                </div>

                <div class="info-item">
                  <div class="info-label">库存数量</div>
                  <template v-if="!editingInfo">
                    <div class="info-content stock-display">
                      <span class="stock-value">{{ shopData.stock }}</span>
                      <span class="stock-unit">件</span>
                    </div>
                  </template>
                  <template v-else>
                    <el-input-number 
                      v-model="infoEdit.stock" 
                      :min="0" 
                      :precision="0" 
                      style="width: 100%"
                      :controls-position="'right'"
                      class="stock-input">
                    </el-input-number>
                  </template>
                </div>
              </div>
            </div>
          </el-card>

          <!-- 标签和特性卡片 -->
          <el-card class="tags-card">
            <div class="card-header">
              <span>标签和特性</span>
              <div class="header-actions">
                <template v-if="!editingTags">
                  <el-button type="text" icon="el-icon-edit" @click="startEditTags">编辑</el-button>
                </template>
                <template v-else>
                  <el-button type="text" icon="el-icon-check" @click="saveTags">保存</el-button>
                  <el-button type="text" icon="el-icon-close" @click="cancelEditTags">取消</el-button>
                </template>
              </div>
            </div>
            <div class="tags-grid" :class="{ 'is-editing': editingTags }">
              <div class="tags-row">
                <div class="tags-item">
                  <div class="tags-label">标签颜色</div>
                  <template v-if="!editingTags">
                    <div class="tags-content">
                      <el-tag :type="shopData.tag_color === 'blue' ? '' : 'danger'" size="small">{{ shopData.tag_color === 'blue' ? '蓝色' : '红色' }}</el-tag>
                    </div>
                  </template>
                  <template v-else>
                    <el-select v-model="tagsEdit.tag_color" placeholder="请选择标签颜色" style="width: 100%">
                      <el-option label="蓝色" value="blue"></el-option>
                      <el-option label="红色" value="red"></el-option>
                    </el-select>
                  </template>
                </div>

                <div class="tags-item">
                  <div class="tags-label">中文标签</div>
                  <template v-if="!editingTags">
                    <div class="tags-content">{{ shopData.tag_cn || '-' }}</div>
                  </template>
                  <template v-else>
                    <el-input v-model="tagsEdit.tag_cn" placeholder="请输入中文标签"></el-input>
                  </template>
                </div>

                <div class="tags-item">
                  <div class="tags-label">维文标签</div>
                  <template v-if="!editingTags">
                    <div class="tags-content">{{ shopData.tag_ug || '-' }}</div>
                  </template>
                  <template v-else>
                    <el-input v-model="tagsEdit.tag_ug" placeholder="ئۇيغۇرچە بەلگىنى كىرگۈزۈڭ"></el-input>
                  </template>
                </div>
              </div>

              <div class="tags-row">
                <div class="tags-item">
                  <div class="tags-label">中文特性</div>
                  <template v-if="!editingTags">
                    <div class="tags-content">{{ shopData.feature_cn || '-' }}</div>
                  </template>
                  <template v-else>
                    <el-input v-model="tagsEdit.feature_cn" placeholder="请输入中文特性"></el-input>
                  </template>
                </div>

                <div class="tags-item">
                  <div class="tags-label">维文特性</div>
                  <template v-if="!editingTags">
                    <div class="tags-content">{{ shopData.feature_ug || '-' }}</div>
                  </template>
                  <template v-else>
                    <el-input v-model="tagsEdit.feature_ug" placeholder="ئۇيغۇرچە ئالاھىدىلىكىنى كىرگۈزۈڭ"></el-input>
                  </template>
                </div>
              </div>

              <div class="tags-row">
                <div class="tags-item">
                  <div class="tags-label">中文促销词</div>
                  <template v-if="!editingTags">
                    <div class="tags-content">{{ shopData.label_cn || '-' }}</div>
                  </template>
                  <template v-else>
                    <el-input v-model="tagsEdit.label_cn" placeholder="请输入中文促销词"></el-input>
                  </template>
                </div>

                <div class="tags-item">
                  <div class="tags-label">维文促销词</div>
                  <template v-if="!editingTags">
                    <div class="tags-content">{{ shopData.label_ug || '-' }}</div>
                  </template>
                  <template v-else>
                    <el-input v-model="tagsEdit.label_ug" placeholder="ئۇيغۇرچە سېتىش سۆزىنى كىرگۈزۈڭ"></el-input>
                  </template>
                </div>
              </div>
            </div>
          </el-card>

          <!-- 商品描述卡片 -->
          <el-card class="desc-card">
            <div class="card-header">
              <span>商品描述</span>
              <div class="header-actions">
                <template v-if="!editingDesc">
                  <el-button type="text" icon="el-icon-edit" @click="startEditDesc">编辑</el-button>
                </template>
                <template v-else>
                  <el-button type="text" icon="el-icon-check" @click="saveDesc">保存</el-button>
                  <el-button type="text" icon="el-icon-close" @click="cancelEditDesc">取消</el-button>
                </template>
              </div>
            </div>
            <div class="desc-grid" :class="{ 'is-editing': editingDesc }">
              <div class="desc-cn">
                <div class="desc-label">中文描述</div>
                <template v-if="!editingDesc">
                  <div class="desc-content">{{ shopData.desc_cn }}</div>
                </template>
                <template v-else>
                  <el-input type="textarea" v-model="descEdit.desc_cn" placeholder="请输入中文描述" :rows="4"></el-input>
                </template>
              </div>
              <div class="desc-ug">
                <div class="desc-label">ئۇيغۇرچە تونۇشتۇرۇلىشى</div>
                <template v-if="!editingDesc">
                  <div class="desc-content">{{ shopData.desc_ug }}</div>
                </template>
                <template v-else>
                  <el-input 
                    type="textarea" 
                    v-model="descEdit.desc_ug" 
                    placeholder="ئۇيغۇرچە تونۇشتۇرۇلىشىنى كىرگۈزۈڭ" 
                    :rows="4"
                    class="ug-input">
                  </el-input>
                </template>
              </div>
            </div>
          </el-card>
        </div>

        <!-- 右侧内容列 -->
        <div class="right-column">
          <!-- 分类卡片 -->
          <el-card class="category-card">
            <div class="card-header">
              <span>商品分类</span>
              <div class="header-actions">
                <template v-if="!editingCategory">
                  <el-button type="text" icon="el-icon-edit" @click="startEditCategory">编辑</el-button>
                </template>
                <template v-else>
                  <el-button type="text" icon="el-icon-check" @click="saveCategory">保存</el-button>
                  <el-button type="text" icon="el-icon-close" @click="cancelEditCategory">取消</el-button>
                </template>
              </div>
            </div>
            <div class="category-content">
              <template v-if="!editingCategory">
                <div class="category-item">
                  <div class="category-label">分类</div>
                  <div class="category-value">
                    <div class="category-name">{{ shopData.cate_name_cn || '-' }}</div>
                  </div>
                </div>
              </template>
              <template v-else>
                <div class="category-item">
                  <div class="category-label">分类</div>
                  <el-select 
                    v-model="categoryEdit.category_id" 
                    placeholder="请选择商品分类"
                    style="width: 100%">
                    <el-option
                      v-for="item in categoryList"
                      :key="item.category_id"
                      :label="item.name_cn"
                      :value="item.category_id">
                    </el-option>
                  </el-select>
                </div>
              </template>
            </div>
          </el-card>

          <!-- 商品图片卡片 -->
          <el-card class="images-card">
            <div class="card-header">
              <span>商品图片</span>
              <div class="header-actions">
                <template v-if="!editingImages">
                  <el-button type="text" icon="el-icon-edit" @click="startEditImages">编辑</el-button>
                </template>
                <template v-else>
                  <el-button type="text" icon="el-icon-check" @click="saveImages">保存</el-button>
                  <el-button type="text" icon="el-icon-close" @click="cancelEditImages">取消</el-button>
                </template>
              </div>
            </div>
            <div class="images-wrapper">
              <div class="cover-grid">
                <div class="cover-item">
                  <div class="label-cn">中文封面</div>
                  <div class="image-container cover-shape">
                    <template v-if="shopData.cover_cn">
                      <el-image 
                        :src="getImageUrl(shopData.cover_cn)"
                        :preview-src-list="[getImageUrl(shopData.cover_cn)]">
                      </el-image>
                      <div class="image-actions" v-if="editingImages">
                        <el-button 
                          type="danger" 
                          icon="el-icon-delete" 
                          class="delete-button"
                          circle 
                          size="mini" 
                          @click="removeImage('cover_cn')">
                        </el-button>
                      </div>
                    </template>
                    <template v-else>
                      <div class="upload-box cover-shape">
                        <el-upload
                          class="image-uploader"
                          :action="getUploadUrl('cover_cn')"
                          :headers="getHeaders()"
                          :show-file-list="false"
                          :on-success="(res) => uploaded(res, 'cover_cn')"
                          :before-upload="beforeUpload">
                          <i class="el-icon-plus"></i>
                          <div class="upload-text">上传封面</div>
                        </el-upload>
                      </div>
                    </template>
                  </div>
                </div>
                
                <div class="cover-item">
                  <div class="label-ug">ئۇيغۇرچە مۇقاۋا</div>
                  <div class="image-container cover-shape">
                    <template v-if="shopData.cover_ug">
                      <el-image 
                        :src="getImageUrl(shopData.cover_ug)"
                        :preview-src-list="[getImageUrl(shopData.cover_ug)]">
                      </el-image>
                      <div class="image-actions" v-if="editingImages">
                        <el-button 
                          type="danger" 
                          icon="el-icon-delete" 
                          class="delete-button"
                          circle 
                          size="mini" 
                          @click="removeImage('cover_ug')">
                        </el-button>
                      </div>
                    </template>
                    <template v-else>
                      <div class="upload-box cover-shape">
                        <el-upload
                          class="image-uploader"
                          :action="getUploadUrl('cover_ug')"
                          :headers="getHeaders()"
                          :show-file-list="false"
                          :on-success="(res) => uploaded(res, 'cover_ug')"
                          :before-upload="beforeUpload">
                          <i class="el-icon-plus"></i>
                          <div class="upload-text">مۇقاۋىنى چىقىرىڭ</div>
                        </el-upload>
                      </div>
                    </template>
                  </div>
                </div>
              </div>

              <div class="carousel-grid">
                <div class="carousel-column">
                  <div class="label-cn">中文轮播图</div>
                  <div class="image-list">
                    <draggable 
                      class="image-list"
                      v-model="shopData.swipers_cn"
                      :disabled="!editingImages"
                      @end="onCarouselDragEnd('cn')">
                      <div v-for="(url, index) in shopData.swipers_cn" 
                           :key="index" 
                           class="image-item carousel-shape">
                        <el-image 
                          :src="getImageUrl(url)"
                          :preview-src-list="shopData.swipers_cn.map(img => getImageUrl(img))">
                        </el-image>
                        <div class="image-actions" v-if="editingImages">
                          <el-button 
                            type="danger" 
                            icon="el-icon-delete" 
                            class="delete-button"
                            circle 
                            size="mini"
                            @click="removeImage('swipers_cn', index)">
                          </el-button>
                        </div>
                      </div>
                      <div v-if="editingImages" class="upload-box carousel-shape">
                        <el-upload
                          class="image-uploader"
                          :action="getUploadUrl('swipers_cn')"
                          :headers="getHeaders()"
                          :show-file-list="false"
                          :on-success="(res) => uploaded(res, 'swipers_cn')"
                          :before-upload="beforeUpload">
                          <i class="el-icon-plus"></i>
                          <div class="upload-text">上传轮播图</div>
                        </el-upload>
                      </div>
                    </draggable>
                  </div>
                </div>
                
                <div class="carousel-column">
                  <div class="label-ug">ئۇيغۇرچە ئايلانما تەگلىكلەر</div>
                  <div class="image-list">
                    <draggable 
                      class="image-list"
                      v-model="shopData.swipers_ug"
                      :disabled="!editingImages"
                      @end="onCarouselDragEnd('ug')">
                      <div v-for="(url, index) in shopData.swipers_ug" 
                           :key="index" 
                           class="image-item carousel-shape">
                        <el-image 
                          :src="getImageUrl(url)"
                          :preview-src-list="shopData.swipers_ug.map(img => getImageUrl(img))">
                        </el-image>
                        <div class="image-actions" v-if="editingImages">
                          <el-button 
                            type="danger" 
                            icon="el-icon-delete" 
                            class="delete-button"
                            circle 
                            size="mini"
                            @click="removeImage('swipers_ug', index)">
                          </el-button>
                        </div>
                      </div>
                      <div v-if="editingImages" class="upload-box carousel-shape">
                        <el-upload
                          class="image-uploader"
                          :action="getUploadUrl('swipers_ug')"
                          :headers="getHeaders()"
                          :show-file-list="false"
                          :on-success="(res) => uploaded(res, 'swipers_ug')"
                          :before-upload="beforeUpload">
                          <i class="el-icon-plus"></i>
                          <div class="upload-text">تەگلىكلەرنى چىقىرىڭ</div>
                        </el-upload>
                      </div>
                    </draggable>
                  </div>
                </div>
              </div>

              <div class="poster-grid">
                <div class="poster-column">
                  <div class="label-cn">中文详情图</div>
                  <div class="image-list">
                    <draggable 
                      class="image-list"
                      v-model="shopData.posters_cn"
                      :disabled="!editingImages"
                      @end="onPosterDragEnd('cn')">
                      <div v-for="(url, index) in shopData.posters_cn" 
                           :key="index" 
                           class="image-item poster-shape">
                        <el-image 
                          :src="getImageUrl(url)"
                          :preview-src-list="shopData.posters_cn.map(img => getImageUrl(img))">
                        </el-image>
                        <div class="image-actions" v-if="editingImages">
                          <el-button 
                            type="danger" 
                            icon="el-icon-delete" 
                            class="delete-button"
                            circle 
                            size="mini"
                            @click="removeImage('posters_cn', index)">
                          </el-button>
                        </div>
                      </div>
                      <div v-if="editingImages" class="upload-box poster-shape">
                        <el-upload
                          class="image-uploader"
                          :action="getUploadUrl('posters_cn')"
                          :headers="getHeaders()"
                          :show-file-list="false"
                          :on-success="(res) => uploaded(res, 'posters_cn')"
                          :before-upload="beforeUpload">
                          <i class="el-icon-plus"></i>
                          <div class="upload-text">上传详情图</div>
                        </el-upload>
                      </div>
                    </draggable>
                  </div>
                </div>
                
                <div class="poster-column">
                  <div class="label-ug">ئۇيغۇرچە مەزمۇن رەسىملىرى</div>
                  <div class="image-list">
                    <draggable 
                      class="image-list"
                      v-model="shopData.posters_ug"
                      :disabled="!editingImages"
                      @end="onPosterDragEnd('ug')">
                      <div v-for="(url, index) in shopData.posters_ug" 
                           :key="index" 
                           class="image-item poster-shape">
                        <el-image 
                          :src="getImageUrl(url)"
                          :preview-src-list="shopData.posters_ug.map(img => getImageUrl(img))">
                        </el-image>
                        <div class="image-actions" v-if="editingImages">
                          <el-button 
                            type="danger" 
                            icon="el-icon-delete" 
                            class="delete-button"
                            circle 
                            size="mini"
                            @click="removeImage('posters_ug', index)">
                          </el-button>
                        </div>
                      </div>
                      <div v-if="editingImages" class="upload-box poster-shape">
                        <el-upload
                          class="image-uploader"
                          :action="getUploadUrl('posters_ug')"
                          :headers="getHeaders()"
                          :show-file-list="false"
                          :on-success="(res) => uploaded(res, 'posters_ug')"
                          :before-upload="beforeUpload">
                          <i class="el-icon-plus"></i>
                          <div class="upload-text">مەزمۇن رەسىمىنى چىقىرىڭ</div>
                        </el-upload>
                      </div>
                    </draggable>
                  </div>
                </div>
              </div>
            </div>
          </el-card>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getToken } from '@/utils/auth'
import { IMAGE_URL, BASE_URL } from '@/utils/urls'
import draggable from 'vuedraggable'

export default {
  name: 'ShopDetails',
  components: {
    draggable
  },
  data() {
    return {
      imageURL: IMAGE_URL,
      serverURL: BASE_URL,
      statusMap: {
        1: { text: '在售', type: 'success' },
        2: { text: '售罄', type: 'warning' },
        3: { text: '下架', type: 'info' }
      },
      shopData: {
        title_cn: '',
        title_ug: '',
        cover_cn: '',
        cover_ug: '',
        price: 0,
        stock: 0,
        desc_cn: '',
        desc_ug: '',
        swipers_cn: [],
        swipers_ug: [],
        posters_cn: [],
        posters_ug: [],
        need_addr: true,
        status: 1,
        prod_id: 0,
        created_at: '',
        updated_at: '',
        is_deleted: 0,
        deleted_at: '',
        deleted_by: '',
        view_count: 0,
        sales_count: 0,
        created_by: '',
        updated_by: '',
        tag_color: '',
        tag_cn: '',
        tag_ug: '',
        label_cn: '',
        label_ug: '',
        feature_cn: '',
        feature_ug: '',
        category_id: '',
        cate_name_cn: ''
      },
      editingTitle: false,
      editingDesc: false,
      editingInfo: false,
      editingImages: false,
      editingTags: false,
      editingCategory: false,
      titleEdit: {},
      descEdit: {},
      infoEdit: {},
      tagsEdit: {},
      categoryEdit: {},
      creatorInfo: {},
      updaterInfo: {},
      categoryList: []
    }
  },
  created() {
    this.getShopDetails()
    this.fetchCategories()
  },
  methods: {
    async getShopDetails() {
      const prodId = this.$route.params.id
      try {
        const res = await this.$http.get(`shop/${prodId}`)
        this.shopData = res.data
        
        if (this.shopData.created_by) {
          await this.getUserInfo(this.shopData.created_by, 'creator')
        }
        if (this.shopData.updated_by) {
          await this.getUserInfo(this.shopData.updated_by, 'updater')
        }
      } catch (err) {
        this.$message.error(err.response?.data?.message || '获取商品详情失败')
      }
    },

    async getUserInfo(userId, type) {
      try {
        const res = await this.$http.get(`users/${userId}`)
        if (type === 'creator') {
          this.creatorInfo = res.data
        } else if (type === 'updater') {
          this.updaterInfo = res.data
        }
      } catch (err) {
        console.error(`获取${type === 'creator' ? '创建者' : '更新者'}信息失败:`, err)
      }
    },

    goBack() {
      this.$router.push('/admin/shop')
    },

    handleDelete() {
      this.$confirm('确认删除该商品?', '提示', {
        type: 'error'
      }).then(async () => {
        await this.$http({ method: 'delete', url: `shop/${this.$route.params.id}` })
        this.$message.success('删除成功')
        this.goBack()
      }).catch(err => {
        this.$message.error(err.response?.data?.message || '删除失败')
      })
    },

    async handleRestore() {
      const prodId = this.$route.params.id
      try {
        await this.$http.put(`shop/${prodId}`, { is_deleted: 0 })
        this.$message.success('恢复成功')
        this.getShopDetails()
      } catch (err) {
        this.$message.error(err.response?.data?.message || '恢复失败')
      }
    },

    getStatusText(status) {
      return this.statusMap[status]?.text || '未知状态'
    },

    getStatusType(status) {
      return this.statusMap[status]?.type || ''
    },

    formatDateTime(date) {
      if (!date) return '-';
      return new Date(date).toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      });
    },

    startEditTitle() {
      this.editingTitle = true
      this.titleEdit = {
        title_cn: this.shopData.title_cn,
        title_ug: this.shopData.title_ug
      }
    },

    async saveTitle() {
      try {
        if (!this.titleEdit.title_cn || !this.titleEdit.title_ug) {
          this.$message.warning('中文和维文标题都不能为空')
          return
        }

        await this.$http.put(`shop/${this.$route.params.id}`, {
          title_cn: this.titleEdit.title_cn,
          title_ug: this.titleEdit.title_ug
        })
        
        this.$message.success('标题更新成功')
        this.editingTitle = false
        this.getShopDetails()
      } catch (err) {
        this.$message.error(err.response?.data?.message || '标题更新失败')
      }
    },

    cancelEditTitle() {
      this.editingTitle = false
      this.titleEdit = {}
    },

    startEditDesc() {
      this.editingDesc = true
      this.descEdit = {
        desc_cn: this.shopData.desc_cn,
        desc_ug: this.shopData.desc_ug
      }
    },

    async saveDesc() {
      try {
        if (!this.descEdit.desc_cn || !this.descEdit.desc_ug) {
          this.$message.warning('中文和维文描述都不能为空')
          return
        }

        await this.$http.put(`shop/${this.$route.params.id}`, {
          desc_cn: this.descEdit.desc_cn,
          desc_ug: this.descEdit.desc_ug
        })
        
        this.$message.success('商品描述更新成功')
        this.editingDesc = false
        this.getShopDetails()
      } catch (err) {
        this.$message.error(err.response?.data?.message || '商品描述更新失败')
      }
    },

    cancelEditDesc() {
      this.$confirm('确认取消编辑？未保存的修改将丢失', '提示', {
        type: 'warning'
      }).then(() => {
        this.editingDesc = false
        this.descEdit = {}
      }).catch(() => {})
    },

    startEditInfo() {
      this.editingInfo = true
      this.infoEdit = {
        status: this.shopData.status,
        price: this.shopData.price,
        stock: this.shopData.stock,
        need_addr: this.shopData.need_addr
      }
    },

    async saveInfo() {
      try {
        if (this.infoEdit.price < 0 || this.infoEdit.stock < 0) {
          this.$message.warning('价格和库存不能为负数')
          return
        }

        await this.$http.put(`shop/${this.$route.params.id}`, {
          status: this.infoEdit.status,
          price: this.infoEdit.price,
          stock: this.infoEdit.stock,
          need_addr: this.infoEdit.need_addr
        })

        this.$message.success('基本信息更新成功')
        this.editingInfo = false
        this.getShopDetails()
      } catch (err) {
        this.$message.error(err.response?.data?.message || '基本信息更新失败')
      }
    },

    cancelEditInfo() {
      this.editingInfo = false
      this.infoEdit = {}
    },

    async toggleNeedAddr(value) {
      const prodId = this.$route.params.id
      try {
        await this.$http.put(`shop/${prodId}`, { need_addr: value })
      } catch (err) {
        this.$message.error(err.response?.data?.message || '更新失败')
        this.shopData.need_addr = !value
      }
    },

    startEditImages() {
      this.editingImages = true
    },

    cancelEditImages() {
      this.editingImages = false
    },

    getUploadUrl(imageType) {
      return `${this.serverURL}/api/v1/shop/images/${imageType}?prod_name=${this.shopData.title_cn || 'temp'}`
    },

    getHeaders() {
      const token = getToken()
      return {
        'Authorization': `Bearer ${token}`
      }
    },

    getImageUrl(path) {
      return path.startsWith('http') ? path : `${this.imageURL}${path}`
    },

    beforeUpload(file) {
      const isImage = /^image\/(jpeg|png|gif|jpg)$/.test(file.type)
      const isLt10M = file.size / 1024 / 1024 < 10

      if (!isImage) {
        this.$message.error('只能上传 JPG/PNG/GIF 格式的图片!')
        return false
      }
      if (!isLt10M) {
        this.$message.error('图片大小不能超过 10MB!')
        return false
      }
      return true
    },

    async uploaded(response, type) {
      if (!response.data) {
        this.$message.error('上传失败')
        return
      }

      const imageUrl = response.data
      const updateData = {}
      
      if (type === 'swipers_cn' || type === 'swipers_ug' || 
          type === 'posters_cn' || type === 'posters_ug') {
        updateData[type] = [...this.shopData[type], imageUrl]
      } else {
        updateData[type] = imageUrl
      }

      try {
        await this.$http.put(`shop/${this.$route.params.id}`, updateData)
        this.$message.success('图片上传成功')
        this.getShopDetails()
      } catch (err) {
        this.$message.error('图片上传失败')
      }
    },

    async removeImage(type, index) {
      try {
        const confirmResult = await this.$confirm('确认删除该图片?', '提示', {
          type: 'warning'
        })

        if (confirmResult === 'confirm') {
          const updateData = {}
          
          if (type === 'swipers_cn' || type === 'swipers_ug' || 
              type === 'posters_cn' || type === 'posters_ug') {
            const newImages = [...this.shopData[type]]
            newImages.splice(index, 1)
            updateData[type] = newImages
          } else {
            updateData[type] = ''
          }

          await this.$http.put(`shop/${this.$route.params.id}`, updateData)
          this.$message.success('图片删除成功')
          this.getShopDetails()
        }
      } catch (err) {
        if (err !== 'cancel') {
          this.$message.error('删除失败')
        }
      }
    },

    async onCarouselDragEnd(type) {
      try {
        const updateData = {}
        if (type === 'cn') {
          updateData.swipers_cn = this.shopData.swipers_cn
        } else {
          updateData.swipers_ug = this.shopData.swipers_ug
        }

        await this.$http.put(`shop/${this.$route.params.id}`, updateData)
        this.$message.success('顺序已更新')
      } catch (err) {
        this.$message.error('顺序更新失败')
        this.getShopDetails()
      }
    },

    async onPosterDragEnd(type) {
      try {
        const updateData = {}
        if (type === 'cn') {
          updateData.posters_cn = this.shopData.posters_cn
        } else {
          updateData.posters_ug = this.shopData.posters_ug
        }

        await this.$http.put(`shop/${this.$route.params.id}`, updateData)
        this.$message.success('顺序已更新')
      } catch (err) {
        this.$message.error('顺序更新失败')
        this.getShopDetails()
      }
    },

    async saveImages() {
      try {
        await this.$http.put(`shop/${this.$route.params.id}`, {
          cover_cn: this.shopData.cover_cn,
          cover_ug: this.shopData.cover_ug,
          swipers_cn: this.shopData.swipers_cn,
          swipers_ug: this.shopData.swipers_ug,
          posters_cn: this.shopData.posters_cn,
          posters_ug: this.shopData.posters_ug
        })
        
        this.$message.success('图片更新成功')
        this.editingImages = false
        this.getShopDetails()
      } catch (err) {
        this.$message.error(err.response?.data?.message || '图片更新失败')
      }
    },

    startEditTags() {
      this.editingTags = true
      this.tagsEdit = {
        tag_color: this.shopData.tag_color,
        tag_cn: this.shopData.tag_cn,
        tag_ug: this.shopData.tag_ug,
        label_cn: this.shopData.label_cn,
        label_ug: this.shopData.label_ug,
        feature_cn: this.shopData.feature_cn,
        feature_ug: this.shopData.feature_ug
      }
    },

    async saveTags() {
      try {
        await this.$http.put(`shop/${this.$route.params.id}`, {
          tag_color: this.tagsEdit.tag_color,
          tag_cn: this.tagsEdit.tag_cn,
          tag_ug: this.tagsEdit.tag_ug,
          label_cn: this.tagsEdit.label_cn,
          label_ug: this.tagsEdit.label_ug,
          feature_cn: this.tagsEdit.feature_cn,
          feature_ug: this.tagsEdit.feature_ug
        })
        
        this.$message.success('标签和特性更新成功')
        this.editingTags = false
        this.getShopDetails()
      } catch (err) {
        this.$message.error(err.response?.data?.message || '标签和特性更新失败')
      }
    },

    cancelEditTags() {
      this.editingTags = false
      this.tagsEdit = {}
    },

    async fetchCategories() {
      try {
        const res = await this.$http.get('shop-cates', {
          params: {
            page: 1,
            size: 100
          }
        })
        if (!res.success) return this.$message.error(res.message)
        this.categoryList = res.data
      } catch (err) {
        this.$message.error(err.response?.data?.message || '获取分类列表失败')
      }
    },

    startEditCategory() {
      this.editingCategory = true
      this.categoryEdit = {
        category_id: this.shopData.category_id || null,
      }
    },

    async saveCategory() {
      try {
        await this.$http.put(`shop/${this.$route.params.id}`, {
          category_id: this.categoryEdit.category_id
        })
        
        this.$message.success('分类更新成功')
        this.editingCategory = false

        this.getShopDetails()
      } catch (err) {
        this.$message.error(err.response?.data?.message || '分类更新失败')
      }
    },

    cancelEditCategory() {
      this.editingCategory = false
      this.categoryEdit = {}
    }
  }
}
</script>

<style lang="less" scoped>
.shop-details {
  min-height: 100vh;
  overflow-x: hidden;
  box-sizing: border-box;

  .content-grid {
    display: grid;
    grid-template-columns: minmax(0, 2fr) minmax(0, 1fr);
    gap: 20px;
    margin: 0 auto;

    @media screen and (max-width: 1200px) {
      grid-template-columns: 1fr;
      
      .right-column {
        order: 2;
      }
      
      .left-column {
        order: 1;
        transform: translateY(20px);
      }
    }
  }
}

.el-card {
  &.title-card,
  &.info-card,
  &.desc-card,
  &.images-card,
  &.tags-card {
    margin-bottom: 20px;
    border-radius: 8px;

    .card-header {
      position: relative;
      padding: 8px 20px;
      border-radius: 8px;
      background: #fafafa;
      display: flex;
      align-items: center;
      justify-content: space-between;

      span {
        font-size: 14px;
        font-weight: 500;
        color: #1f2937;
        padding-left: 10px;
        position: relative;

        &::before {
          content: '';
          position: absolute;
          left: 0;
          top: 50%;
          transform: translateY(-50%);
          width: 2px;
          height: 12px;
          background: #1890ff;
          border-radius: 1px;
        }
      }

      .header-actions {
        position: absolute;
        right: 16px;
        top: 50%;
        transform: translateY(-50%);
        z-index: 1;
      }
    }
  }
}

// 顶部操作栏样式
.action-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  background: #fff;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
  margin-bottom: 20px;
  border-radius: 8px;

  .date-info {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 48px;
    color: #888;
    font-size: 13px;

    &.deleted {
      color: #ff4d4f;
    }
  }

  @media (max-width: 768px) {
    flex-wrap: wrap;
    gap: 8px;
    align-items: center;
    margin-bottom: 0px;

    .date-info {
      display: none;
    }

    .left-actions,
    .right-actions {
      flex-shrink: 0;
    }
  }
}

// 标题卡片样式
.title-card {
  .title-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 24px;
    padding: 20px;

    .title-cn, .title-ug {
      .title-label {
        font-size: 13px;
        color: #666;
        margin-bottom: 12px;
      }

      .title-content {
        background: #fafafa;
        padding: 9px 15px;
        border-radius: 4px;
        border: 1px solid #f0f0f0;
        font-size: 14px;
        color: #262626;
      }
    }

    .title-ug {
      direction: rtl;

      .title-label {
        text-align: right;
      }

      .el-input__inner {
        direction: rtl;
        text-align: right;
      }

      .title-content {
        text-align: right;
      }
    }
  }
}

// 基本信息卡片样式
.info-card {
  .info-grid {
    padding: 20px;

    .info-row {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 24px;
      margin-bottom: 20px;

      &:last-child {
        margin-bottom: 0;
      }
    }

    .info-item {
      background: #fafafa;
      padding: 12px 16px;
      border-radius: 8px;
      border: 1px solid #f0f0f0;

      .info-label {
        font-size: 13px;
        color: #666;
        margin-bottom: 8px;
      }

      .info-content {
        font-size: 14px;
        color: #262626;
        overflow: hidden;

        &.price-display {
          display: flex;
          align-items: baseline;
          gap: 2px;

          .price-symbol {
            font-size: 16px;
            color: #ff4d4f;
            font-weight: 500;
          }

          .price-value {
            font-size: 20px;
            color: #ff4d4f;
            font-weight: 600;
            line-height: 1;
          }
        }

        &.stock-display {
          display: flex;
          align-items: baseline;
          gap: 4px;

          .stock-value {
            font-size: 20px;
            color: #262626;
            font-weight: 500;
          }

          .stock-unit {
            font-size: 14px;
            color: #666;
          }
        }
      }

      .price-input {
        :deep(.el-input-number__decrease),
        :deep(.el-input-number__increase) {
          background-color: #fff;
          border-color: #dcdfe6;
        }

        :deep(.el-input__inner) {
          font-size: 16px;
          font-weight: 500;
          color: #ff4d4f;
        }
      }

      .stock-input {
        :deep(.el-input-number__decrease),
        :deep(.el-input-number__increase) {
          background-color: #fff;
          border-color: #dcdfe6;
        }

        :deep(.el-input__inner) {
          font-size: 16px;
          font-weight: 500;
        }
      }
    }
  }
}

// 标签和特性卡片样式
.tags-card {
  .tags-grid {
    padding: 20px;

    .tags-row {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 24px;
      margin-bottom: 20px;

      &:first-child {
        grid-template-columns: repeat(3, 1fr);
      }

      &:last-child {
        margin-bottom: 0;
      }
    }

    .tags-item {
      background: #fafafa;
      padding: 12px 16px;
      border-radius: 8px;
      border: 1px solid #f0f0f0;

      .tags-label {
        font-size: 13px;
        color: #666;
        margin-bottom: 8px;
      }

      .tags-content {
        font-size: 14px;
        color: #262626;
      }
    }
  }
}

// 描述卡片样式
.desc-card {
  .desc-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 24px;
    padding: 20px;

    .desc-cn, .desc-ug {
      .desc-label {
        font-size: 13px;
        color: #666;
        margin-bottom: 12px;
      }

      .desc-content {
        background: #fafafa;
        padding: 9px 15px;
        border-radius: 4px;
        border: 1px solid #f0f0f0;
        font-size: 14px;
        color: #262626;
        min-height: 100px;
        white-space: pre-wrap;
      }
    }

    .desc-ug {
      direction: rtl;

      .desc-label {
        text-align: right;
      }

      .desc-content {
        text-align: right;
        font-family: 'Alpsoft Jurnal', sans-serif;
      }

      .ug-input {
        :deep(.el-textarea__inner) {
          font-family: 'Alpsoft Jurnal', sans-serif;
          text-align: right;
          direction: rtl;
        }
      }
    }
  }
}

// 图片相关样式
.images-card {
  .images-wrapper {
    padding: 20px;
  }

  .cover-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 24px;
    margin-bottom: 24px;
  }

  .carousel-grid,
  .poster-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 24px;
    margin-bottom: 24px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  .label-cn {
    font-size: 13px;
    color: #666;
    margin-bottom: 12px;
    direction: ltr;
  }

  .label-ug {
    font-size: 13px;
    color: #666;
    margin-bottom: 12px;
    direction: rtl;
  }

  .image-container {
    position: relative;
    width: 100%;
    border-radius: 8px;
    overflow: hidden;

    :deep(.el-image) {
      width: 100%;
      height: 100%;
      display: block;

      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }
    }

    .image-actions {
      position: absolute;
      top: 8px;
      right: 8px;
      opacity: 0;
      transition: opacity 0.3s;
      z-index: 1;
    }

    &:hover .image-actions {
      opacity: 1;
    }
  }

  .image-list {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(110px, 1fr));
    gap: 8px;

    .image-item {
      position: relative;
      border-radius: 8px;
      overflow: hidden;
      background-color: #f8f8f8;

      :deep(.el-image) {
        width: 100%;
        height: 100%;
        display: block;

        img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
      }

      .image-actions {
        position: absolute;
        top: 8px;
        right: 8px;
        opacity: 0;
        transition: opacity 0.3s;
        z-index: 1;
      }

      &:hover .image-actions {
        opacity: 1;
      }
    }
  }
}

.cover-shape {
  aspect-ratio: 16/9;
}

.carousel-shape {
  aspect-ratio: 16/10;
}

.poster-shape {
  aspect-ratio: 3/2;
}

.upload-box {
  border-radius: 8px;
  overflow: hidden;
  background-color: #fafafa;
  cursor: pointer;
  transition: background-color 0.3s;

  &:hover {
    background-color: #f5f5f5;
  }

  .image-uploader {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }

  .el-icon-plus {
    font-size: 20px;
    color: #8c939d;
    margin-bottom: 8px;
  }

  .upload-text {
    font-size: 12px;
    color: #666;
  }
}

.el-button {
  padding: 10px 20px;
  transition: all 0.2s ease;

  &.delete-button {
    padding: 7px !important;
  }

  &--text {
    padding: 4px 8px;
    font-size: 13px;

    &:hover {
      background: rgba(24, 144, 255, 0.06);
      color: #1890ff;
    }
  }
}

.el-input :deep(.el-input__inner) {
  font-family: inherit;
}

.el-tag {
  margin: 0;
  padding: 0 10px;
}

// 分类卡片样式
.category-card {
  margin-bottom: 20px;
  border-radius: 8px;

  .card-header {
    position: relative;
    padding: 8px 20px;
    border-radius: 8px;
    background: #fafafa;
    display: flex;
    align-items: center;
    justify-content: space-between;

    span {
      font-size: 14px;
      font-weight: 500;
      color: #1f2937;
      padding-left: 10px;
      position: relative;

      &::before {
        content: '';
        position: absolute;
        left: 0;
        top: 50%;
        transform: translateY(-50%);
        width: 2px;
        height: 12px;
        background: #1890ff;
        border-radius: 1px;
      }
    }

    .header-actions {
      position: absolute;
      right: 16px;
      top: 50%;
      transform: translateY(-50%);
      z-index: 1;
    }
  }

  .category-content {
    padding: 16px;

    .category-item {
      background: #fafafa;
      padding: 12px 16px;
      border-radius: 8px;
      border: 1px solid #f0f0f0;

      .category-label {
        font-size: 13px;
        color: #666;
        margin: 6px 0 8px 0;
      }

      .category-value {
        .category-name {
          font-size: 14px;
          color: #262626;
        }

        .no-category {
          font-size: 14px;
          color: #999;
          font-style: italic;
        }
      }
    }
  }
}

@media (max-width: 768px) {
  .shop-details {
    padding: 0 5px;
  }

  .content-grid {
    grid-template-columns: 1fr !important;
    gap: 10px;

    .left-column,
    .right-column {
      order: initial;
      transform: none;
    }
  }

  .el-card {
    margin-bottom: 10px !important;
    padding: 8px;
    overflow: hidden;
    min-width: 0;
  }

  .content-grid,
  .left-column,
  .right-column {
    min-width: 0;
  }

  .card-header {
    padding: 6px 10px !important;

    span {
      font-size: 12px !important;
      padding-left: 6px !important;
    }

    .header-actions {
      right: 8px;
    }
  }

  .title-grid {
    grid-template-columns: 1fr 1fr !important;
    gap: 6px !important;
    padding: 10px !important;
    min-width: 0;

    &.is-editing {
      grid-template-columns: 1fr !important;
    }
  }

  .desc-grid {
    grid-template-columns: 1fr !important;
    gap: 6px !important;
    padding: 10px !important;
    min-width: 0;
  }

  .info-grid {
    padding: 10px !important;
    min-width: 0;

    .info-row {
      grid-template-columns: 1fr 1fr !important;
      gap: 6px !important;
      margin-bottom: 6px !important;
    }

    &.is-editing {
      .info-row {
        grid-template-columns: 1fr !important;
      }
    }

    .info-item {
      min-width: 0;
    }
  }

  .tags-grid {
    padding: 10px !important;
    min-width: 0;

    .tags-row {
      grid-template-columns: 1fr 1fr !important;
      gap: 6px !important;
      margin-bottom: 6px !important;

      &:first-child {
        grid-template-columns: 1fr 1fr 1fr !important;
      }
    }

    &.is-editing {
      .tags-row {
        grid-template-columns: 1fr !important;

        &:first-child {
          grid-template-columns: 1fr !important;
        }
      }
    }

    .tags-item {
      min-width: 0;
    }
  }

  .images-wrapper {
    padding: 10px !important;

    .image-list {
      grid-template-columns: repeat(auto-fill, minmax(70px, 1fr)) !important;
      gap: 4px;
    }

    .image-item {
      aspect-ratio: 16/9;
    }
  }

  .category-card {
    .category-content {
      padding: 10px !important;
    }
  }

  .el-tag {
    font-size: 10px !important;
    padding: 1px 5px !important;
  }

  .el-input__inner {
    font-size: 12px;
    height: 32px;
  }

  .el-textarea__inner {
    font-size: 12px;
  }

  .el-select .el-input__inner {
    font-size: 12px;
    height: 32px;
  }

  .el-input-number {
    :deep(.el-input__inner) {
      font-size: 12px;
      height: 32px;
    }
  }

  .el-dialog {
    width: 95% !important;
    margin: 0 auto;
    top: 5vh;
  }
}
</style>